/**
 * Универсальный компонент выбора даты и диапазона.
 * Подробнее: см. docs.md в папке компонента.
 */

import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import cn from 'classnames';
import moment, { Moment } from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';

import { formatDate } from 'shared/helpers';
import { dateLocale } from 'shared/model/dateLocale';
import { Icon } from 'shared/ui';

import { openCalendar } from './lib/openCalendar';
import cls from './UiDateInput.module.css';

interface BaseDateInputProps<TError = { isError: boolean; errorMessage?: string }> {
    value: string;
    onChange: (date: string) => void;
    onCancel?: () => void;
    onRangeChange?: (range: { start: string; end: string }) => void;
    minDate?: string;
    maxDate?: string;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    required?: boolean;
    error?: TError;
    inputClassName?: string;
    calendarClassName?: string;
    calendarIconClassNames?: string;
    className?: string;
    showIcon?: boolean;
    iconFill?: string;
    mode?: 'single' | 'range';
    autoApply?: boolean;
    showDropdowns?: boolean;
    autoUpdateInput?: boolean;
}

interface Picker {
    startDate: Moment;
    endDate: Moment;
    element: {
        val: (value?: string) => void;
    };
}

export const UiDateInput = forwardRef<HTMLInputElement, BaseDateInputProps>((props, ref) => {
    const {
        value,
        onChange,
        onCancel,
        onRangeChange,
        minDate = '2020-01-01',
        maxDate = '2030-01-01',
        placeholder,
        disabled = false,
        label,
        required = false,
        error,
        inputClassName = '',
        calendarIconClassNames = '',
        className,
        showIcon = true,
        iconFill = '#818998',
        mode = 'single',
        autoApply = true,
        showDropdowns = true,
        autoUpdateInput = false,
        calendarClassName,
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    const initialSettings = {
        singleDatePicker: mode === 'single',
        locale: dateLocale,
        minDate: moment(minDate, 'YYYY-MM-DD'),
        maxDate: moment(maxDate, 'YYYY-MM-DD'),
        autoApply,
        showDropdowns,
        autoUpdateInput,
    };

    const handleApply = (event: any, picker: Picker) => {
        const formatted = formatDate(picker.startDate, 'DD.MM.YYYY');
        onChange(formatted);
        if (onRangeChange) {
            onRangeChange({
                start: formatDate(picker.startDate, 'DD.MM.YYYY'),
                end: formatDate(picker.endDate, 'DD.MM.YYYY'),
            });
        }
    };

    const handleCancel = () => {
        onChange('');
        onCancel?.();
    };

    return (
        <div className={cn(cls.wrapper, className, { [cls.disabled]: disabled })}>
            {label && (
                <label className={cls.label}>
                    {label}
                    {required && <span>*</span>}
                </label>
            )}
            <div className={cn(cls.calendar, calendarClassName, { [cls.disabled]: disabled })}>
                <DateRangePicker
                    initialSettings={initialSettings}
                    onApply={handleApply}
                    onCancel={handleCancel}
                >
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={cn(cls.input, cls.calendarInput, inputClassName)}
                        type="text"
                    />
                </DateRangePicker>
                {showIcon && (
                    <div
                        className={cn(cls.calendarIconWrapper, calendarIconClassNames)}
                        onClick={() => {
                            if (!disabled && !openCalendar(inputRef)) {
                                setTimeout(() => openCalendar(inputRef), 50);
                            }
                        }}
                        style={{
                            cursor: disabled || !inputRef.current ? 'default' : 'pointer',
                            pointerEvents: !inputRef.current ? 'none' : 'auto',
                        }}
                    >
                        <Icon iconName="taskCalendar" fill={iconFill} />
                    </div>
                )}
            </div>
            {error?.isError && <span className={cls.errorMessage}>{error.errorMessage}</span>}
        </div>
    );
});
